/**
 * 스캔 가이드 박스 계산 — **화면(캔버스) 기준** 카드 비율(63:88) 박스를 만들고,
 * QuadOverlay 의 cover 매핑을 역산해 프레임 정규화 좌표로 변환한다.
 *
 * 배경: QuadOverlay 기본 가이드는 프레임(720×1280) 기준 고정 비율이라, 화면이
 * 프레임보다 길쭉하면(20:9 등) cover 매핑 후 가이드가 세로로 늘어나 위아래에
 * 붙어버린다 (실기기 확인). 화면 기준으로 원하는 박스를 먼저 정하고 역변환한다.
 */
import type { QuadCorners } from "@duckflex/capacitor-live-vision";

const CARD_RATIO = 88 / 63;
/** 가이드 폭 = 캔버스 폭의 68% (양옆 16% 여백) */
const WIDTH_RATIO = 0.68;
/** 세로 상한 — 컨트롤/상단 크롬을 피해 캔버스 높이의 58%까지만 */
const MAX_HEIGHT_RATIO = 0.58;
/** 가이드 중심의 세로 위치 (셔터가 아래 있으므로 중앙보다 약간 위) */
const CENTER_Y_RATIO = 0.44;

/**
 * @param canvasW/H 캔버스 backing-store 픽셀
 * @param frameW/H  분석 프레임 크기 (표시방향 — 기본 720×1280)
 */
/**
 * 브래킷 대기 위치가 들어갈 수 있는 세로 구간 (캔버스 픽셀). 위는 인식 결과
 * 레일 바닥, 아래는 셔터 줄 꼭대기 — ScanPage 가 `.guideBand` 를 재서 넘긴다.
 */
export interface GuideBand {
  top: number;
  bottom: number;
}

/**
 * 캔버스 픽셀 기준 가이드 박스 코너 4점 (TL·TR·BR·BL) — 락온 오버레이의
 * 대기(idle) 위치. 비율 상수만 쓰므로 CSS px / backing-store px 어느 쪽을
 * 넣어도 그 좌표계로 나온다.
 *
 * `band` 를 주면 **그 구간 안에** 넣는다 (2026-08-28 실기기 제보: 카드가 담기면
 * 상단 인식 레일이 브래킷 윗변을 덮었다). 비율만으로는 못 푸는 문제다 — 레일과
 * 셔터 줄은 화면 높이에 비례하지 않고 px 로 고정이며, 안전영역까지 얹히므로
 * 기기마다 남는 자리가 다르다. 구간은 CSS 가 정하고(`.guideBand`) 여기서는
 * 그 안에 카드 비율 상자를 세운다.
 *
 * band 없이 부르면 종전 비율 배치 그대로다 — 레일이 없는 자리(정지 사진 편집)와
 * 기존 테스트가 그 경로를 쓴다.
 */
export function computeCardGuideBox(
  canvasW: number,
  canvasH: number,
  band?: GuideBand,
): { x: number; y: number }[] {
  let boxW = canvasW * WIDTH_RATIO;
  let boxH = boxW * CARD_RATIO;
  const limitH = band ? band.bottom - band.top : canvasH * MAX_HEIGHT_RATIO;
  if (boxH > limitH) {
    boxH = limitH;
    boxW = boxH / CARD_RATIO;
  }
  const cx = canvasW / 2;
  const cy = band ? (band.top + band.bottom) / 2 : canvasH * CENTER_Y_RATIO;
  return [
    { x: cx - boxW / 2, y: cy - boxH / 2 },
    { x: cx + boxW / 2, y: cy - boxH / 2 },
    { x: cx + boxW / 2, y: cy + boxH / 2 },
    { x: cx - boxW / 2, y: cy + boxH / 2 },
  ];
}

export function computeCardGuide(
  canvasW: number,
  canvasH: number,
  frameW = 720,
  frameH = 1280,
): QuadCorners {
  // 캔버스 기준 목표 박스
  const box = computeCardGuideBox(canvasW, canvasH);
  const left = box[0].x;
  const right = box[1].x;
  const top = box[0].y;
  const bottom = box[2].y;

  // cover 매핑 역산: 캔버스 픽셀 → 프레임 정규화 (QuadOverlay/mapQuadToView 와 동일 공식)
  const scale = Math.max(canvasW / frameW, canvasH / frameH);
  const ox = (canvasW - frameW * scale) / 2;
  const oy = (canvasH - frameH * scale) / 2;
  const toFrame = (x: number, y: number) => ({
    x: (x - ox) / (frameW * scale),
    y: (y - oy) / (frameH * scale),
  });

  return [
    toFrame(left, top),
    toFrame(right, top),
    toFrame(right, bottom),
    toFrame(left, bottom),
  ] as QuadCorners;
}

// cornersInsideGuide / cornersNearGuideCorners (검출 코너의 가이드 포함·가득 채움 판정)는
// 자동 셔터의 가이드 기하 게이트와 함께 2026-08-20 제거됐다 — useAutoShutter 주석 참조.
